import * as React from "react";
import DataTable, { TableColumn, ExpanderComponentProps } from "react-data-table-component";
import moment from "moment-timezone";
import NumberFormat from "react-number-format";
import { Form, Dropdown, Modal, Button } from "react-bootstrap";
import AddFavoriteCandidate from "../../favorites/components/AddFavoriteCandidate";
import { AllNotesModal } from "../../notes/components/ProfileNotes";
import type { Note } from "../../notes/components/ProfileNotes";
import SBSendMessage from "./SBChat/SBSendMessage";
import type {
  ApplicantProfile as Profile,
  ApplicationInvitationRecord as Invitation,
} from "../../../shared/types/domainModels";

// Types
// Columns marked hide: MOBILE_BREAKPOINT drop out at or below this width so the
// table fits a phone. Name, Invitations and Actions stay; the rest is a
// profile-click away. Matches ApplicantsTable and the page stylesheet.
const MOBILE_BREAKPOINT = 768;

interface GroupedInvitation {
  profile: Profile;
  invitations: Invitation[];
  invitationCount: number;
  earliestInvitation: string;
  latestInvitation: string;
  hasUnseen: boolean;
}

interface ApplicantsInvitationsTableProps {
  applicants: Invitation[];
  favorites: number[];
  authenticity_token: string;
  notes: Note[];
  load_notes: () => void;
  loaded: boolean;
  timezone: string;
}

// Group invitations by profile
const groupInvitationsByProfile = (invitations: Invitation[]): GroupedInvitation[] => {
  const grouped = invitations.reduce((acc, invitation) => {
    const profileId = invitation.profile.id;
    if (!acc[profileId]) {
      acc[profileId] = {
        profile: invitation.profile,
        invitations: [],
        invitationCount: 0,
        earliestInvitation: invitation.created_at,
        latestInvitation: invitation.created_at,
        hasUnseen: false
      };
    }
    acc[profileId].invitations.push(invitation);
    acc[profileId].invitationCount++;
    if (!invitation.seen) {
      acc[profileId].hasUnseen = true;
    }
    // Update earliest/latest
    if (new Date(invitation.created_at) < new Date(acc[profileId].earliestInvitation)) {
      acc[profileId].earliestInvitation = invitation.created_at;
    }
    if (new Date(invitation.created_at) > new Date(acc[profileId].latestInvitation)) {
      acc[profileId].latestInvitation = invitation.created_at;
    }
    return acc;
  }, {} as Record<number, GroupedInvitation>);

  return Object.values(grouped);
};

// Expandable Job Invitations Component
const ExpandedJobInvitationsComponent: React.FC<ExpanderComponentProps<GroupedInvitation> & {
  timezone: string;
}> = ({ data, timezone }) => {
  const [showAll, setShowAll] = React.useState(false);
  const displayLimit = 5;
  const invitationsToShow = showAll ? data.invitations : data.invitations.slice(0, displayLimit);
  const hasMore = data.invitations.length > displayLimit;

  return (
    <div style={{
      padding: '1.5rem',
      background: 'linear-gradient(to bottom, rgba(8, 94, 200, 0.02), rgba(23, 111, 201, 0.01))',
      borderTop: '1px solid #e8ecef'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        marginBottom: '1rem',
        color: '#034ba6',
        fontWeight: 600,
        fontSize: '0.875rem'
      }}>
        <i className="fa fa-envelope"></i>
        Job Invitations ({data.invitations.length})
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {invitationsToShow.map((invitation) => {
          const job_title = invitation.job?.job_title || invitation.job?.job_type?.title || 'Unknown Job';
          const practice_name = invitation.job?.practice?.name || 'Unknown Practice';
          const invited_date = moment(invitation.created_at).tz(timezone).format("ll");
          const position_type = invitation.job?.position_type || 'permanent';

          return (
            <div
              key={invitation.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1.5fr 1fr 1fr 100px',
                gap: '1rem',
                alignItems: 'center',
                padding: '1rem',
                background: 'white',
                borderRadius: '0.5rem',
                border: '1px solid #e8ecef',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
              }}
            >
              {/* Job Title */}
              <div>
                <a
                  href={`/jobs/${invitation.job?.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#085ec8',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    transition: 'color 0.3s ease'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.color = '#71ad08'}
                  onMouseOut={(e) => e.currentTarget.style.color = '#085ec8'}
                >
                  <i className="fa fa-briefcase me-2" style={{ color: '#085ec8' }}></i>
                  {job_title}
                </a>
                <div style={{ fontSize: '0.8rem', color: '#6c757d', marginTop: '0.25rem' }}>
                  {practice_name}
                </div>
              </div>

              {/* Invited Date */}
              <div style={{ fontSize: '0.875rem' }}>
                <span style={{ color: '#6c757d' }}>
                  Invited
                </span>
                <br />
                {invited_date}
              </div>

              {/* Position Type Badge */}
              <div>
                <span
                  className="badge-modern"
                  style={{
                    backgroundColor: position_type === 'temp' ? '#f1630d' : '#176fc9',
                    color: 'white',
                    whiteSpace: 'nowrap',
                    padding: '0.375rem 0.75rem',
                    fontSize: '0.75rem'
                  }}
                >
                  {position_type === 'temp' ? 'Temp' : 'Permanent'}
                </span>
              </div>

              {/* New Badge */}
              <div>
                {!invitation.seen && (
                  <span className="badge-modern badge-modern--warning" style={{ whiteSpace: 'nowrap' }}>
                    New
                  </span>
                )}
              </div>

              {/* Actions */}
              <div style={{ textAlign: 'center' }}>
                <a
                  href={`/jobs/${invitation.job?.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '0.375rem',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    textDecoration: 'none',
                    background: 'linear-gradient(135deg, #085ec8, #176fc9)',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 6px rgba(8, 94, 200, 0.25)'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 10px rgba(8, 94, 200, 0.35)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 6px rgba(8, 94, 200, 0.25)';
                  }}
                >
                  <i className="fa fa-external-link me-1"></i>
                  View
                </a>
              </div>
            </div>
          );
        })}
      </div>

      {/* Show More / Show Less Button */}
      {hasMore && (
        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <button
            onClick={() => setShowAll(!showAll)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #085ec8, #176fc9)',
              color: 'white',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 6px rgba(8, 94, 200, 0.25)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 10px rgba(8, 94, 200, 0.35)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 6px rgba(8, 94, 200, 0.25)';
            }}
          >
            <i className={`fa fa-chevron-${showAll ? 'up' : 'down'}`}></i>
            {showAll ? 'Show Less' : `Show ${data.invitations.length - displayLimit} More`}
          </button>
        </div>
      )}
    </div>
  );
};

// Column Definitions for Grouped Invitations
const dataColumns = ({
  favorites,
  authenticity_token,
  timezone,
  notes,
  load_notes,
  loaded,
  onShowNotes
}: {
  favorites: number[];
  authenticity_token: string;
  timezone: string;
  notes: Note[];
  load_notes: () => void;
  loaded: boolean;
  onShowNotes: (profile: Profile) => void;
}): TableColumn<GroupedInvitation>[] => [
    {
      name: "Avatar",
      sortable: false,
      cell: (row) => {
        const profile_url = `/profiles/${row.profile.id}`;
        return (
          <a href={profile_url} target="_blank" rel="noopener noreferrer">
            <img
              src={row.profile.avatar_url}
              className="rounded-circle"
              alt={row.profile.fullname}
              style={{
                width: '60px',
                height: '60px',
                objectFit: 'cover',
                border: '3px solid #e8ecef',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => e.currentTarget.style.border = '3px solid #085ec8'}
              onMouseOut={(e) => e.currentTarget.style.border = '3px solid #e8ecef'}
            />
          </a>
        );
      },
      width: '90px',
      center: true,
      hide: MOBILE_BREAKPOINT
    },
    {
      name: "Name",
      selector: (row) => row.profile.fullname,
      sortable: true,
      cell: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <a
            href={`/profiles/${row.profile.id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#085ec8',
              textDecoration: 'none',
              fontWeight: 700,
              fontSize: '1rem',
              transition: 'color 0.3s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.color = '#71ad08'}
            onMouseOut={(e) => e.currentTarget.style.color = '#085ec8'}
          >
            {row.profile.fullname}
          </a>
          <AddFavoriteCandidate
            profile={row.profile}
            favorited_init={favorites.includes(row.profile.id)}
            authenticity_token={authenticity_token}
            expandDirection="left"
          />
        </div>
      ),
      grow: 2
    },
    {
      name: "Location",
      selector: (row) => `${row.profile.city}, ${row.profile.state}`,
      sortable: true,
      cell: (row) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.875rem' }}>
          <i className="fa fa-map-marker" style={{ color: '#085ec8' }}></i>
          {row.profile.city}, {row.profile.state}
        </span>
      ),
      grow: 1.5,
      hide: MOBILE_BREAKPOINT
    },
    {
      name: "Desired Rate",
      selector: (row) => row.profile.hourly_rate || 0,
      sortable: true,
      cell: (row) => row.profile.hourly_rate ? (
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.875rem', fontWeight: 600 }}>
          <i className="fa fa-money" style={{ color: '#71ad08' }}></i>
          ${row.profile.hourly_rate}/hr
        </span>
      ) : (
        <span style={{ color: '#6c757d', fontSize: '0.875rem' }}>N/A</span>
      ),
      grow: 1,
      hide: MOBILE_BREAKPOINT
    },
    {
      name: "Invitations",
      selector: (row) => row.invitationCount,
      sortable: true,
      cell: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span className="badge-modern badge-modern--info" style={{
            fontSize: '0.875rem',
            padding: '0.375rem 0.75rem'
          }}>
            {row.invitationCount} {row.invitationCount === 1 ? 'Job' : 'Jobs'}
          </span>
          {row.hasUnseen && (
            <span className="badge-modern badge-modern--warning" style={{ fontSize: '0.75rem' }}>
              New
            </span>
          )}
        </div>
      ),
      grow: 1.2
    },
    {
      name: "Actions",
      sortable: false,
      cell: (row) => {
        const phone_mask = row.profile.phone && row.profile.phone.length > 10 ? "+# (###) ###-####" : "(###) ###-####";
        const profileNotes = notes.filter(note => row.profile.id === note.profile_id);

        return (
          <div className="d-flex justify-content-center align-items-center gap-2" style={{ width: '100%' }}>
            <Dropdown>
              <Dropdown.Toggle
                variant="primary"
                size="sm"
                style={{
                  fontSize: '0.875rem',
                  padding: '0.5rem 0.875rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  color: 'white',
                  background: 'linear-gradient(135deg, #085ec8, #176fc9)',
                  transition: 'all 0.3s ease',
                  fontWeight: 600,
                  boxShadow: '0 2px 6px rgba(8, 94, 200, 0.25)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.375rem'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 10px rgba(8, 94, 200, 0.35)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 6px rgba(8, 94, 200, 0.25)';
                }}
              >
                <i className="fa fa-ellipsis-h"></i>
                Actions
              </Dropdown.Toggle>
              <Dropdown.Menu align="end" className="context-menu">
                <Dropdown.Item as="button" type="button" className="context-menu__item" onClick={() => onShowNotes(row.profile)}>
                  <i className="fa fa-sticky-note" aria-hidden="true"></i>
                  Notes {profileNotes.length > 0 && `(${profileNotes.length})`}
                </Dropdown.Item>
                <Dropdown.Divider />
                {row.profile.phone && (
                  <Dropdown.Item className="context-menu__item" href={`tel:${row.profile.phone}`}>
                    <i className="fa fa-phone" aria-hidden="true"></i>
                    <NumberFormat
                      value={row.profile.phone}
                      displayType="text"
                      type="text"
                      format={phone_mask}
                      mask="_"
                    />
                  </Dropdown.Item>
                )}
                <Dropdown.Item as="div" className="context-menu__item" style={{ cursor: 'pointer' }}>
                  <SBSendMessage profileId={row.profile.id.toString()} variant="link" />
                </Dropdown.Item>
                <Dropdown.Item className="context-menu__item" href={`/profiles/${row.profile.id}`} target="_blank">
                  <i className="fa fa-user" aria-hidden="true"></i>
                  View Profile
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        );
      },
      grow: 1,
      center: true
    }
  ];

// Filter Bar Component
interface FilterBarProps {
  filters: {
    nameFilter: string;
    dateFilter: string;
  };
  onFiltersChange: (filters: any) => void;
}

function FilterBar({ filters, onFiltersChange }: FilterBarProps) {
  const clearAllFilters = () => {
    onFiltersChange({
      nameFilter: "",
      dateFilter: ""
    });
  };

  const hasActiveFilters = filters.nameFilter || filters.dateFilter;

  return (
    <div
      style={{
        background: 'white',
        border: '1px solid #e8ecef',
        borderRadius: '0.75rem',
        padding: '0.875rem',
        marginBottom: '1.5rem',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)'
      }}
    >
      <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: '0.75rem' }}>
        <div className="d-flex gap-3 flex-wrap align-items-center">
          <div style={{ width: "250px", position: "relative", marginBottom: 0 }}>
            <Form.Group style={{ marginBottom: 0 }}>
              <div className="position-relative">
                <Form.Control
                  type="text"
                  placeholder="Filter by Name"
                  value={filters.nameFilter}
                  onChange={(e) => onFiltersChange({ ...filters, nameFilter: e.target.value })}
                  style={{
                    marginBottom: 0,
                    borderRadius: '0.5rem',
                    border: '1px solid #e8ecef',
                    padding: '0.5rem 0.75rem',
                    fontSize: '0.95rem',
                    transition: 'all 0.3s ease'
                  }}
                />
                {filters.nameFilter && (
                  <div
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      zIndex: 5,
                      color: '#6c757d'
                    }}
                    onClick={() => onFiltersChange({ ...filters, nameFilter: "" })}
                  >
                    <i className="fa fa-times" aria-hidden="true"></i>
                  </div>
                )}
              </div>
            </Form.Group>
          </div>
          <div style={{ width: "180px", position: "relative", marginBottom: 0 }}>
            <Form.Group style={{ marginBottom: 0 }}>
              <div className="position-relative">
                <Form.Select
                  value={filters.dateFilter}
                  onChange={(e) => onFiltersChange({ ...filters, dateFilter: e.target.value })}
                  style={{
                    marginBottom: 0,
                    borderRadius: '0.5rem',
                    border: '1px solid #e8ecef',
                    padding: '0.5rem 0.75rem',
                    fontSize: '0.95rem',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <option value="">All Dates</option>
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                </Form.Select>
              </div>
            </Form.Group>
          </div>
          <button
            className="btn-modern btn-modern--ghost btn-modern--sm"
            onClick={clearAllFilters}
            style={{
              opacity: hasActiveFilters ? 1 : 0.6
            }}
            disabled={!hasActiveFilters}
          >
            <i className="fa fa-times"></i> Clear filters
          </button>
        </div>
      </div>
    </div>
  );
}

// Main Component
export default function ApplicantsInvitationsTable({
  applicants,
  favorites,
  authenticity_token,
  notes,
  load_notes,
  loaded,
  timezone
}: ApplicantsInvitationsTableProps) {
  const [showNotesModal, setShowNotesModal] = React.useState(false);
  const [selectedProfile, setSelectedProfile] = React.useState<Profile | null>(null);

  const [filters, setFilters] = React.useState({
    nameFilter: "",
    dateFilter: ""
  });

  const handleShowNotes = (profile: Profile) => {
    setSelectedProfile(profile);
    setShowNotesModal(true);
  };

  // Filter logic - filter on individual invitations before grouping
  const filteredInvitations = React.useMemo(() => {
    return applicants.filter((invitation) => {
      // Name filter
      if (filters.nameFilter && !invitation.profile.fullname.toLowerCase().includes(filters.nameFilter.toLowerCase())) {
        return false;
      }

      // Date filter
      if (filters.dateFilter) {
        const invitationDate = moment(invitation.created_at);
        const now = moment();

        switch (filters.dateFilter) {
          case "today":
            if (!invitationDate.isSame(now, 'day')) return false;
            break;
          case "week":
            if (!invitationDate.isSame(now, 'week')) return false;
            break;
          case "month":
            if (!invitationDate.isSame(now, 'month')) return false;
            break;
        }
      }

      return true;
    });
  }, [applicants, filters]);

  // Group invitations by profile
  const groupedInvitations = React.useMemo(() => {
    return groupInvitationsByProfile(filteredInvitations);
  }, [filteredInvitations]);

  // Sort: profiles with unseen invitations first, then by latest invitation date
  const sortedGroupedInvitations = React.useMemo(() => {
    return [...groupedInvitations].sort((a, b) => {
      // Unseen first
      if (a.hasUnseen && !b.hasUnseen) return -1;
      if (!a.hasUnseen && b.hasUnseen) return 1;
      // Then by latest invitation
      return new Date(b.latestInvitation).getTime() - new Date(a.latestInvitation).getTime();
    });
  }, [groupedInvitations]);

  const columns = dataColumns({
    favorites,
    authenticity_token,
    timezone,
    notes,
    load_notes,
    loaded,
    onShowNotes: handleShowNotes
  });

  return (
    <div>
      <FilterBar
        filters={filters}
        onFiltersChange={setFilters}
      />
      <div
        style={{
          background: 'white',
          border: '1px solid #e8ecef',
          borderRadius: '0.75rem',
          padding: '0.75rem',
          marginBottom: '1.5rem',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
          transition: 'box-shadow 0.3s ease',
          overflow: 'auto'
        }}
      >
        <DataTable
          data={sortedGroupedInvitations}
          columns={columns}
          defaultSortFieldId={5} // Sort by invitation count by default
          defaultSortAsc={false}
          expandableRows
          expandableRowExpanded={() => true} // Auto-expand all rows
          expandableRowsComponent={(props) => (
            <ExpandedJobInvitationsComponent
              {...props}
              timezone={timezone}
            />
          )}
          responsive
          customStyles={{
            responsiveWrapper: {
              style: {
                overflowX: 'visible',
                overflowY: 'visible'
              }
            },
            table: {
              style: {
                width: '100%',
                tableLayout: 'auto'
              }
            },
            headCells: {
              style: {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#6c757d',
                paddingTop: '0.75rem',
                paddingBottom: '0.75rem',
                paddingLeft: '0.5rem',
                paddingRight: '0.5rem'
              }
            },
            cells: {
              style: {
                paddingLeft: '0.5rem',
                paddingRight: '0.5rem',
                paddingTop: '0.75rem',
                paddingBottom: '0.75rem',
                overflow: 'visible'
              }
            },
            expanderButton: {
              style: {
                color: '#085ec8',
                '&:hover': {
                  color: '#71ad08'
                }
              }
            }
          }}
        />
      </div>

      {/* All Notes Modal */}
      {selectedProfile && (
        <AllNotesModal
          show={showNotesModal}
          onHide={() => setShowNotesModal(false)}
          profile={selectedProfile}
          notes={notes.filter(note => selectedProfile.id === note.profile_id)}
          authenticity_token={authenticity_token}
          loadNotesCallback={load_notes}
        />
      )}
    </div>
  );
}
